import React, { useState, useEffect } from 'react';
import { aboutUsApi } from '../../services/api';

interface ValueState {
  title: string;
  description: string;
}

const ManageSiteContentPage: React.FC = () => {
  const [historyText, setHistoryText] = useState<string>('');
  const [values, setValues] = useState<ValueState[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchAboutUs = async () => {
      try {
        setLoading(true);
        const response = await aboutUsApi.getAboutUs();
        setHistoryText(response.data.data.about.history);
        setValues(response.data.data.about.values);
        setError(null);
      } catch (err: any) {
        setError('Failed to load About Us content.');
      } finally {
        setLoading(false);
      }
    };
    fetchAboutUs();
  }, []);

  const handleHistoryChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setHistoryText(e.target.value);
  };

  const handleValueChange = (index: number, field: keyof ValueState, value: string) => {
    const newValues = [...values];
    newValues[index] = { ...newValues[index], [field]: value };
    setValues(newValues);
  };

  const handleAddValue = () => {
    setValues([...values, { title: '', description: '' }]);
  };

  const handleRemoveValue = (index: number) => {
    setValues(values.filter((_, i) => i !== index));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSuccess(null);
    setError(null);
    try {
      await aboutUsApi.updateAboutUs({ history: historyText, values });
      setSuccess('Content updated successfully!');
    } catch (err: any) {
      setError('Failed to update content.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold text-white">Manage Site Content</h2>
        <p className="text-neutral-400">Update general text content displayed on the public website. Changes here are saved and reflected live.</p>
      </div>
      {loading ? (
        <div className="flex justify-center items-center min-h-[100px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <p className="text-red-500 mb-4">{error}</p>
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-8">
          {/* Institute History */}
          <div className="bg-neutral-dark p-6 rounded-lg shadow-xl">
            <h3 className="text-xl font-semibold text-white mb-4">Institute History</h3>
            <div className="space-y-4">
              <label htmlFor="historyText" className="block text-sm font-medium text-neutral-300 mb-1">
                "Our History" Section Content
              </label>
              <textarea
                id="historyText"
                name="historyText"
                rows={8}
                value={historyText}
                onChange={handleHistoryChange}
                className="w-full bg-neutral-darker p-3 rounded border border-neutral-600 text-neutral-light focus:ring-primary focus:border-primary transition-colors"
              />
            </div>
          </div>

          {/* Professional Values */}
          <div className="bg-neutral-dark p-6 rounded-lg shadow-xl">
            <h3 className="text-xl font-semibold text-white mb-4">Learning The Study Room Way</h3>
            <div className="space-y-6">
              {values.map((value, index) => (
                <div key={index} className="p-4 border border-neutral-700 rounded-md space-y-3 relative">
                  <h4 className="text-md font-semibold text-neutral-200">Value #{index + 1}</h4>
                  <div>
                    <label htmlFor={`valueTitle-${index}`} className="block text-sm font-medium text-neutral-300 mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      id={`valueTitle-${index}`}
                      value={value.title}
                      onChange={(e) => handleValueChange(index, 'title', e.target.value)}
                      className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 text-neutral-light focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div>
                    <label htmlFor={`valueDescription-${index}`} className="block text-sm font-medium text-neutral-300 mb-1">
                      Description
                    </label>
                    <textarea
                      id={`valueDescription-${index}`}
                      rows={3}
                      value={value.description}
                      onChange={(e) => handleValueChange(index, 'description', e.target.value)}
                      className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 text-neutral-light focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveValue(index)}
                    className="absolute top-2 right-2 text-red-400 hover:text-red-600 text-xs px-2 py-1 rounded"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <div className="flex justify-between items-center pt-2">
                <button
                  type="button"
                  onClick={handleAddValue}
                  className="bg-secondary hover:bg-emerald-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  + Add Value
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-primary hover:bg-primary-dark text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-60"
                >
                  {saving ? 'Saving...' : 'Save All Changes'}
                </button>
              </div>
              {success && <div className="text-green-400 text-sm text-right pt-2">{success}</div>}
              {error && <div className="text-red-400 text-sm text-right pt-2">{error}</div>}
            </div>
          </div>
        </form>
      )}
      <div className="bg-neutral-dark p-6 rounded-lg shadow-xl">
        <h3 className="text-xl font-semibold text-white mb-4">Other Content Areas</h3>
        <p className="text-neutral-400">
            Management for Team Members, specific page images, or more granular content components would be added here in a full system.
            Payment gateway configuration and email template management would also reside in dedicated admin sections, typically requiring backend integration.
        </p>
      </div>
    </div>
  );
};

export default ManageSiteContentPage;